/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// prop-type is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";

// NewUser page components
import FormField from "views/platforms/new-platform/components/FormField";
import FiltersSelection from "views/platforms/new-platform/components/filtersSelection";
import MultiSoftSelect from "components/MultiSelectionSelectbylabel";
import MultiSoftSelectbyValue from "components/MultiSelectionSelect";

import LogoHandler from "../LogoHandler";

import { getData } from "Utils/server";
import { getAuthorizationHeader } from "Utils/helpers";

// Soft UI Dashboard PRO React context
import { useSoftUIController } from "context";

function PlatformConfig({ formData, handleData, id, errorVisibility, allowCreate, errorLevel }) {
  const [editable, setEditable] = useState(false);
  const { formField, values, errors, touched } = formData;
  const {
    platformName,
    platformExchange,
    vlcourtEmail,
    vlcourtPassword,
    queueIdentifier,
    routingKey,
    rmqsSelected,
    settleOnlyBets,
    validateBets,
    receiveSeperateStatistics,
    riskManagement,
    demo,
    hardProviderDown,
    nuRemove,
    compressMessage,
    traderView,
    promos,
    outrights,
  } = formField;
  const {
    platformName: platformNameV,
    platformExchange: platformExchangeV,
    vlcourtEmail: vlcourtEmailV,
    vlcourtPassword: vlcourtPasswordV,
    queueIdentifier: queueIdentifierV,
    routingKey: routingKeyV,
    rmqsSelected: rmqsSelectedV,
    settleOnlyBets: settleOnlyBetsV,
    validateBets: validateBetsV,
    receiveSeperateStatistics: receiveSeperateStatisticsV,
    riskManagement: riskManagementV,
    demo: demoV,
    hardProviderDown: hardProviderDownV,
    nuRemove: nuRemoveV,
    compressMessage: compressMessageV,
    traderView: traderViewV,
    promos: promosV,
    outrights: outrightsV,
  } = values;

  const [onlyBetsChecked, setOnlyBetsChecked] = useState(settleOnlyBetsV);
  const [validateBetsChecked, setValidateBetsChecked] = useState(validateBetsV);
  const [receiveSeperateStatisticsChecked, setreceiveSeperateStatisticsChecked] = useState(
    receiveSeperateStatisticsV
  );
  const [riskManagementChecked, setRiskManagementChecked] = useState(riskManagementV);
  const [demoChecked, setDemoChecked] = useState(demoV);
  const [hardProviderDownChecked, setHardProviderDownChecked] = useState(hardProviderDownV);
  const [nuRemoveChecked, setNuRemoveChecked] = useState(nuRemoveV);
  const [compressMessageChecked, setCompressMessageChecked] = useState(compressMessageV);
  const [traderViewChecked, setTraderViewChecked] = useState(traderViewV);
  const [promosChecked, setPromosChecked] = useState(promosV);
  const [platformLogo, setPlatformLogo] = useState(null);
  const [controller] = useSoftUIController();
  const [availableRMQs, setAvailableRMQs] = useState([]);
  const [availableOutrights, setAvailableOutrights] = useState([
    { value: 1, label: "NO Outrights" },
    { value: 2, label: "ONLY Outrights" },
    { value: 3, label: "ALL" },
  ]);
  const { permissions } = controller;

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const handleChangeOnlyBets = (event) => {
    //console.log(values);
    setOnlyBetsChecked(event.target.checked);
    values.settleOnlyBets = event.target.checked;
    settleOnlyBets.value = event.target.checked;
  };

  const handleChangeValidateBets = (event) => {
    //console.log(values);
    setValidateBetsChecked(event.target.checked);
    values.validateBets = event.target.checked;
    validateBets.value = event.target.checked;
  };

  const handleChangeReceiveSeperateStatistics = (event) => {
    //console.log(values);
    setreceiveSeperateStatisticsChecked(event.target.checked);
    values.receiveSeperateStatistics = event.target.checked;
    receiveSeperateStatistics.value = event.target.checked;
  };

  const handleChangeRiskManagement = (event) => {
    setRiskManagementChecked(event.target.checked);
    values.riskManagement = event.target.checked;
    riskManagement.value = event.target.checked;
  };

  const handleChangeDemo = (event) => {
    setDemoChecked(event.target.checked);
    values.demo = event.target.checked;
    demo.value = event.target.checked;
  };

  const handleChangeHardProviderDown = (event) => {
    setHardProviderDownChecked(event.target.checked);
    values.hardProviderDown = event.target.checked;
    hardProviderDown.value = event.target.checked;
  };

  const handleChangeNuRemove = (event) => {
    setNuRemoveChecked(event.target.checked);
    values.nuRemove = event.target.checked;
    nuRemove.value = event.target.checked;
  };

  const handleChangeCompressMessage = (event) => {
    setCompressMessageChecked(event.target.checked);
    values.compressMessage = event.target.checked;
    compressMessage.value = event.target.checked;
  };

  const handleChangeTraderView = (event) => {
    setTraderViewChecked(event.target.checked);
    values.traderView = event.target.checked;
    traderView.value = event.target.checked;
  };

  const handleChangePromos = (event) => {
    setPromosChecked(event.target.checked);
    values.promos = event.target.checked;
    promos.value = event.target.checked;
  };

  async function loadResource() {
    await sleep(2000);
    let logoname = values.logo;
    let getPlatformEndpoint = `/api/v1/platforms/${id}`;
    await sleep(1000);
    let response = await getData(getPlatformEndpoint, { Authorization: getAuthorizationHeader() });

    await sleep(1000);
    if (response) {
      logoname = response.data[0].logo;
    }

    setPlatformLogo(process.env.REACT_APP_AUTH_URL + "/api/v1/files/preview/" + logoname);
  }

  async function getEntities() {
    if (availableRMQs.length > 0) return;
    let getRmqsListEndpoint = `/api/v1/entities/availableRmqClusters`;
    let rmqs = await getData(getRmqsListEndpoint, { Authorization: getAuthorizationHeader() });

    setAvailableRMQs(
      rmqs.data.map((rmq) => {
        return { value: rmq.id, label: rmq.name };
      })
    );
    // console.log(rmqs);
  }

  useEffect(() => {
    if (values) {
      if (values.logo) loadResource();
      getEntities();
    }
  }, [values]);

  useEffect(() => {
    if (
      permissions.length > 0 &&
      (permissions.indexOf("management-platforms-edit") > -1 || permissions.indexOf("super") > -1)
    ) {
      setEditable(true);
    }
  }, [permissions]);

  return (
    <SoftBox>
      <SoftBox mt={1.625}>
        <Grid container spacing={2}>
          <div id="divImage" style={{ display: allowCreate ? "" : "none" }}></div>
          <LogoHandler id={id} url={platformLogo} allowCreate={allowCreate} />
          <Grid item xs={12} sm={10} className="platform-fields-holder">
            <Grid container>
              <Grid item xs={12} sm={7}>
                <Grid container>
                  <Grid item xs={12} sm={12}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={3} className="platform-fields-holder">
                        <FormField
                          type={platformName.type}
                          label={platformName.label}
                          disabled={!editable}
                          name={platformName.name}
                          value={platformNameV ? platformNameV : ""}
                          placeholder={platformName.placeholder}
                          error={errors.platformName && touched.platformName}
                          success={platformName.length > 0 && !errors.platformName}
                        />
                        <FormField
                          type={platformExchange.type}
                          label={platformExchange.label}
                          disabled={!editable}
                          name={platformExchange.name}
                          value={platformExchangeV ? platformExchangeV : ""}
                          placeholder={platformExchange.placeholder}
                          error={errors.platformExchange && touched.platformName}
                          success={platformExchange.length > 0 && !errors.platformExchange}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={3}
                        className="platform-fields-holder"
                        style={{ display: allowCreate ? "" : "none" }}
                      >
                        <FormField
                          type={vlcourtEmail.type}
                          label={vlcourtEmail.label}
                          disabled={!editable}
                          name={vlcourtEmail.name}
                          value={vlcourtEmailV ? vlcourtEmailV : ""}
                          placeholder={vlcourtEmail.placeholder}
                          error={errors.vlcourtEmail && touched.vlcourtEmail}
                          success={vlcourtEmail.length > 0 && !errors.vlcourtEmail}
                        />
                        <FormField
                          type={vlcourtPassword.type}
                          label={vlcourtPassword.label}
                          disabled={!editable}
                          name={vlcourtPassword.name}
                          value={vlcourtPasswordV ? vlcourtPasswordV : ""}
                          placeholder={vlcourtPassword.placeholder}
                          error={errors.vlcourtPassword && touched.vlcourtPassword}
                          success={vlcourtPassword.length > 0 && !errors.vlcourtPassword}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        className="platform-fields-holder"
                        style={{ display: allowCreate ? "" : "none" }}
                      >
                        <FormField
                          type={queueIdentifier.type}
                          label={queueIdentifier.label}
                          disabled={!editable}
                          name={queueIdentifier.name}
                          value={queueIdentifierV ? queueIdentifierV : ""}
                          placeholder={queueIdentifier.placeholder}
                          error={errors.queueIdentifier && touched.queueIdentifier}
                          success={queueIdentifier.length > 0 && !errors.queueIdentifier}
                        />
                        <FormField
                          type={routingKey.type}
                          label={routingKey.label}
                          disabled={!editable}
                          name={routingKey.name}
                          value={routingKeyV ? routingKeyV : ""}
                          placeholder={routingKey.placeholder}
                          error={errors.routingKey && touched.routingKey}
                          success={routingKey.length > 0 && !errors.routingKey}
                          title={`Possible Dynamic Parts: 

eventType -> {{eventType}}
sportId -> {{sportId}}
eventId -> {{eventId}}
stm -> {{stm}}

Any other static string.`}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <FormField
                      label={rmqsSelected.label}
                      type={rmqsSelected.type}
                      name={rmqsSelected.name}
                      options={availableRMQs}
                      value={rmqsSelectedV}
                      isMulti={true}
                      component={MultiSoftSelect}
                      placeholder={rmqsSelected.placeholder}
                      error={errors.rmqsSelected && touched.rmqsSelected}
                      success={rmqsSelected.length > 0 && !errors.rmqsSelected}
                      className="multiselectwrap"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid container xs={12} sm={5}>
                <Grid
                  item
                  xs={6}
                  sm={6}
                  className="platform-fields-holder"
                  style={{ display: allowCreate ? "" : "none" }}
                >
                  <Grid item xs={12}>
                    <FormField
                      type={settleOnlyBets.type}
                      label={settleOnlyBets.label}
                      name={settleOnlyBets.name}
                      value={settleOnlyBetsV}
                      checked={onlyBetsChecked}
                      onChange={handleChangeOnlyBets}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormField
                      type={validateBets.type}
                      label={validateBets.label}
                      name={validateBets.name}
                      value={validateBetsV}
                      checked={validateBetsChecked}
                      onChange={handleChangeValidateBets}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormField
                      type={receiveSeperateStatistics.type}
                      label={receiveSeperateStatistics.label}
                      name={receiveSeperateStatistics.name}
                      value={receiveSeperateStatisticsV}
                      checked={receiveSeperateStatisticsChecked}
                      onChange={handleChangeReceiveSeperateStatistics}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormField
                      type={riskManagement.type}
                      label={riskManagement.label}
                      name={riskManagement.name}
                      value={riskManagement}
                      checked={riskManagementChecked}
                      onChange={handleChangeRiskManagement}
                    />
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={6}
                  sm={6}
                  className="platform-fields-holder"
                  style={{ display: allowCreate ? "" : "none" }}
                >
                  <Grid item xs={12}>
                    <FormField
                      type={demo.type}
                      label={demo.label}
                      name={demo.name}
                      value={demo}
                      checked={demoChecked}
                      onChange={handleChangeDemo}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormField
                      type={hardProviderDown.type}
                      label={hardProviderDown.label}
                      name={hardProviderDown.name}
                      value={hardProviderDown}
                      checked={hardProviderDownChecked}
                      onChange={handleChangeHardProviderDown}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormField
                      type={nuRemove.type}
                      label={nuRemove.label}
                      name={nuRemove.name}
                      value={nuRemove}
                      checked={nuRemoveChecked}
                      onChange={handleChangeNuRemove}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormField
                      type={compressMessage.type}
                      label={compressMessage.label}
                      name={compressMessage.name}
                      value={compressMessage}
                      checked={compressMessageChecked}
                      onChange={handleChangeCompressMessage}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormField
                      type={traderView.type}
                      label={traderView.label}
                      name={traderView.name}
                      value={traderView}
                      checked={traderViewChecked}
                      onChange={handleChangeTraderView}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormField
                      type={promos.type}
                      label={promos.label}
                      name={promos.name}
                      value={promos}
                      checked={promosChecked}
                      onChange={handleChangePromos}
                    />
                  </Grid>
                </Grid>
                <Grid container xs={12} sm={12}>
                  <Grid item xs={6}></Grid>
                  <Grid item xs={6}>
                    <FormField
                      label={outrights.label}
                      type={outrights.type}
                      name={outrights.name}
                      options={availableOutrights}
                      value={outrightsV}
                      component={MultiSoftSelectbyValue}
                      // placeholder={outrights.placeholder}
                      error={errors.outrights && touched.outrights}
                      success={!errors.outrights}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} style={{ display: allowCreate ? "" : "none" }}>
            <FiltersSelection
              handleData={handleData}
              id={id}
              errorVisibility={errorVisibility}
              errorLevel={errorLevel}
            ></FiltersSelection>
          </Grid>
        </Grid>
      </SoftBox>
    </SoftBox>
  );
}

// typechecking props for UserInfo
PlatformConfig.propTypes = {
  formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  handleData: PropTypes.func,
  id: PropTypes.number,
  errorVisibility: PropTypes.bool,
  allowCreate: PropTypes.bool,
  errorLevel: PropTypes.number,
};

export default PlatformConfig;
